import React, { ComponentType, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { AppState } from '@om-fe/core/core/types';
import { BemMethod, createBem, Direction, getDirection, PlatformTypes } from '@om-fe/utils';
import { ItemWidthType, LayoutType } from '@om-fe/widgets/dist/components/nav/nav.constants';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { WidgetsRouteChangeDetails, WidgetsRouteName } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { SportsNav } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav';
import { SportsNavType } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.constants';
import { useSportsNavList, UseSportsNavStore, useSportsNavTopicStoreCreator } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import getConfig, { OMFE2ConfigModel, PopularEventsConfig } from '@om-fe/core/utils/config';
import { getMainMarketsLimit } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { FavoritesStoreState } from '@om-fe/core/components/favorite-button/favorite-state.constants';
import { FavoritesStoreState as FavoritesStoreStateV2 } from '@om-fe/core/components/favorite-events/favorite-state.constants';
import { FavoriteTeamsStateModel, favoriteTeamsSlice } from '@om-fe/core/components/favorite-teams/favorite-teams.store';
import { favoriteSlice } from '@om-fe/core/components/favorite-events/favorite-state.store';;
import getSlice from '@om-fe/core/utils/get-slice';;
import MatchOfTheDayMatchListContent from './match-of-the-day-content';
import { DEFAULT_MARKETS_HEADER_OPTIONS } from '../match-list/utils/markets-header.utils';
import dayjs from "dayjs";
import { MatchOfTheDayContentTabsIds } from './match-of-the-day-content-tabs';

interface MatchOfTheDayPublicProps {
    readonly hasLink?: boolean;
    readonly activeTab: MatchOfTheDayContentTabsIds;
    readonly onSelectDiscipline: (selectedDisciplineId: (string)) => void;
    readonly selectDisciplineId?: string;
}

type MatchOfTheDayRouteProps = RouteComponentProps<{
    readonly matchOfTheDayDisciplineId: string | undefined,
    readonly matchOfTheDayDisciplineName: string | undefined,
}>;

interface MatchOfTheDayStoreProps {
    readonly breakpoints: BreakpointsState;
    readonly deviceType: PlatformTypes | undefined;
    readonly favoriteTeams?: number;
}

type MatchOfTheDayProps = MatchOfTheDayPublicProps
    & MatchOfTheDayStoreProps
    & MatchOfTheDayRouteProps;

const bem: BemMethod = createBem('NavWrapper');
const OVERVIEW_MATCH_OF_THE_DAY_LIMIT = 20;
const instanceId = 'content';
const config: OMFE2ConfigModel = getConfig();

const MatchOfTheDayComponent: FC<MatchOfTheDayProps> = ({
    breakpoints,
    deviceType,
    match: { params: { matchOfTheDayDisciplineId } },
    favoriteTeams,
    activeTab,
    selectDisciplineId,
    onSelectDiscipline,
}: MatchOfTheDayProps): React.ReactElement => {
    const [disciplineId, setDisciplineId] = useState<string>(matchOfTheDayDisciplineId || selectDisciplineId || '');
    const { selectedLanguage }: I18nContextInterface = useI18NContext();
    const direction: Direction = getDirection(config.enableRtl, selectedLanguage);
    const topicTimezoneOffset: boolean = config.matchListConfig?.topBets?.topicTimezoneOffset ?? false;

    const dateRange = useMemo(() => {
        let startDate = dayjs().startOf('day').unix();
        let endDate = dayjs().endOf('day').unix();

        if (activeTab === MatchOfTheDayContentTabsIds.Tomorrow) {
            startDate = dayjs().add(1,'day').startOf('day').unix();
            endDate = dayjs().add(1,'day').endOf('day').unix();
        }

        return `${startDate}-${endDate}`;
    }, [activeTab]);

    const useStore: UseSportsNavStore = useSportsNavTopicStoreCreator({
        sportsNavType: SportsNavType.NextSportsByTime,
        counterType: undefined,
        hasAllSports: false,
        hasNextEvents: false,
        favorites: favoriteTeams,
        showOnlySportsWithMatches: true,
        virtualStatus: false,
        dateRange,
        topicTimezoneOffset,
    });

    const disciplineList = useSportsNavList(useStore);

    useEffect(() => {
        if ((!disciplineId && disciplineList.length) || !disciplineList.includes(disciplineId)) {
            if (!selectDisciplineId || (disciplineList.length && !disciplineList.includes(selectDisciplineId))) {
                setDisciplineId(disciplineList[0]);
                onSelectDiscipline(disciplineList[0]);
            }
        }

    }, [disciplineId, disciplineList, selectDisciplineId]);

    const onSelectItem = useCallback((details: WidgetsRouteChangeDetails<WidgetsRouteName.Sport>) => {
        setDisciplineId(details.params.sportId!);
        onSelectDiscipline(details.params.sportId!);

    }, []);

    const popularConfig: PopularEventsConfig | undefined = useMemo(() => ({
        eventsNumber: { overview: config?.matchListConfig?.popularEvents?.eventsNumber?.overview },
        marketsHeaderOptions: config?.matchListConfig?.popularEvents?.marketsHeaderOptions || DEFAULT_MARKETS_HEADER_OPTIONS,
    }), []);

    const mainMarketsLimit = useMemo(() => getMainMarketsLimit(breakpoints), [breakpoints]);
    const navItem = useStore((state) => disciplineId ? state.items[disciplineId] : undefined);

    return (
        <div className={bem()}>
            <SportsNav
                useStore={useStore}
                onRouteChange={onSelectItem}
                activeItemId={disciplineId}
                sportRoute={false}
                hasArrows={deviceType === 'desktop'}
                layoutType={LayoutType.SingleRow}
                itemWidthType={ItemWidthType.ItemFullUnequalWidth}
                direction={direction}
                className={bem({ element: 'SportsNav', extra: `${SportsNavType.Popular}SportsNav` })}
                disableEsportNavItem={config.layout?.disableEsportNavItem}
                showIcon
            />
            {disciplineId && navItem && (
                <MatchOfTheDayMatchListContent
                    disciplineId={disciplineId}
                    limitNumberOfEvents={OVERVIEW_MATCH_OF_THE_DAY_LIMIT}
                    mainMarketsLimit={mainMarketsLimit}
                    marketsHeaderOptions={popularConfig.marketsHeaderOptions}
                    instanceId={instanceId}
                    displayChildren={navItem.displayChildren}
                    deviceType={deviceType}
                    dateRange={dateRange}
                />
            )}
        </div>
    );
};

const MatchOfTheDay: ComponentType<MatchOfTheDayPublicProps> = compose(
    connect<MatchOfTheDayStoreProps>(
        (state: AppState): MatchOfTheDayStoreProps => {
            const { breakpoints, deviceInfo } = state;
            const hasFavoriteTeams: boolean = config.favorite?.showFavoriteTeams ?? false;
            let countFavoriteTeams: number = 0;

            if (hasFavoriteTeams) {
                const favorites: FavoritesStoreState | FavoritesStoreStateV2 | undefined = getSlice<FavoritesStoreState | FavoritesStoreStateV2>(state, favoriteSlice);
                const favoriteTeams: FavoriteTeamsStateModel | undefined = getSlice<FavoriteTeamsStateModel>(state, favoriteTeamsSlice);

                if (favorites) {
                    countFavoriteTeams += Object.keys(favorites.favoriteMatchItems).length;
                }

                if (favoriteTeams) {
                    countFavoriteTeams += favoriteTeams?.list ? favoriteTeams.list.length : 0;
                }
            }

            return {
                breakpoints: breakpoints,
                deviceType: deviceInfo?.platform?.type,
                favoriteTeams: hasFavoriteTeams ? countFavoriteTeams : undefined,
            };
        }),
    withRouter,
)(MatchOfTheDayComponent);

export default MatchOfTheDay;
